import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Shield, Users, Building2, ScrollText, Activity, Database, Layers, ArrowRight,
} from "lucide-react";
import { api, extractAxiosError } from "@/lib/api";
import { toast } from "sonner";

const QUICK_LINKS = [
    { to: "/admin/users", label: "Users", desc: "Accounts, roles, and plans", icon: Users },
    { to: "/admin/orgs", label: "Organizations", desc: "Workspaces and ownership", icon: Building2 },
    { to: "/admin/pools", label: "Pools", desc: "All revenue pools platform-wide", icon: Layers },
    { to: "/admin/traffic", label: "Traffic", desc: "API request analytics", icon: Activity },
    { to: "/admin/database", label: "Database", desc: "Browse and edit MongoDB records", icon: Database },
    { to: "/admin/audit", label: "Audit logs", desc: "Superadmin action history", icon: ScrollText },
];

function StatCard({ label, value, sub }) {
    return (
        <div className="surface p-4">
            <p className="label-eyebrow">{label}</p>
            <p className="mt-2 font-heading text-2xl tabular-nums text-white">{value ?? "—"}</p>
            {sub ? <p className="mt-1 text-xs text-white/40">{sub}</p> : null}
        </div>
    );
}

export function AdminDashboardPage() {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/overview");
            setOverview(data);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load platform overview"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const totals = overview?.totals || {};
    const recent = overview?.recent || {};
    const plans = overview?.users_by_plan || {};

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div className="hidden flex-wrap items-end justify-between gap-3 lg:flex">
                <div>
                    <h2 className="font-heading text-xl tracking-tight">Dashboard</h2>
                    <p className="mt-1 text-sm text-white/45">Live platform metrics and admin tools.</p>
                </div>
                <button type="button" onClick={load} className="btn-ghost rounded-md px-3 py-1.5 text-xs" disabled={loading}>
                    Refresh
                </button>
            </div>
            <div className="flex items-center justify-end lg:hidden">
                <button type="button" onClick={load} className="btn-ghost rounded-md px-3 py-1.5 text-xs" disabled={loading}>
                    Refresh
                </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Users" value={loading ? "…" : totals.users} sub={`${recent.new_users_7d ?? 0} new · 7d`} />
                <StatCard label="Organizations" value={loading ? "…" : totals.organizations} sub={`${totals.active_memberships ?? 0} active memberships`} />
                <StatCard label="Pools" value={loading ? "…" : totals.pools} sub={`${totals.payouts ?? 0} payouts recorded`} />
                <StatCard label="API traffic · 24h" value={loading ? "…" : recent.api_requests_24h} sub={`${recent.admin_actions_24h ?? 0} admin actions`} />
            </div>

            {!loading && overview && (
                <div className="grid gap-4 lg:grid-cols-2">
                    <div className="surface p-5">
                        <p className="label-eyebrow">Users by plan</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {Object.entries(plans).map(([plan, count]) => (
                                <span key={plan} className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs capitalize">
                                    {plan} <span className="font-mono text-white/70">{count}</span>
                                </span>
                            ))}
                        </div>
                        <p className="mt-4 text-xs text-white/40">{recent.failed_logins_24h ?? 0} failed login attempts in the last 24 hours.</p>
                    </div>
                    <div className="surface p-5">
                        <p className="label-eyebrow">Database collections</p>
                        <p className="mt-2 text-sm text-white/55">
                            {Object.keys(overview.collection_counts || {}).length} collections tracked.
                            Use the database browser to inspect and edit records safely.
                        </p>
                        <Link to="/admin/database" className="mt-4 inline-flex items-center gap-1.5 text-xs admin-accent-text hover:text-white">
                            Open database browser <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {QUICK_LINKS.map(({ to, label, desc, icon: Icon }) => (
                    <Link
                        key={to}
                        to={to}
                        className="surface block p-5 transition-colors hover:bg-white/[0.03] min-h-[132px]"
                        data-testid={`admin-dash-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <Icon className="admin-accent-icon mb-3 h-5 w-5" />
                        <p className="font-medium text-white">{label}</p>
                        <p className="mt-1 text-xs text-white/45">{desc}</p>
                    </Link>
                ))}
            </div>

            <div className="surface flex items-start gap-3 p-5">
                <Shield className="admin-accent-icon mt-0.5 h-5 w-5 shrink-0" />
                <div>
                    <p className="text-sm font-medium text-white">Superadmin console</p>
                    <p className="mt-1 text-xs text-white/45">
                        All actions are enforced server-side, sensitive fields are masked, and audit logs are append-only.
                    </p>
                </div>
            </div>
        </div>
    );
}
